<script setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { currencyFormatter } from 'next/message/bubbles/TiktokShop/helper/formatter.js';
import Icon from 'next/icon/Icon.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import EmptyMessage from 'components/widgets/EmptyMessage.vue';

const props = defineProps({
  currentChat: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const searchQuery = ref('');
const store = useStore();

const uiFlags = useMapGetter('tiktokShop/getUIFlags');
const products = useMapGetter('tiktokShop/searchProducts');
const fetchingProductsMessage = 'Searching for products...';

const productImage = product => {
  return product.meta.image_urls ? product.meta.image_urls[0] : null;
};

const productPrice = product => {
  const price = product.meta?.price;
  return currencyFormatter(price || 0);
};

const productStatusMapping = product => {
  switch (product.status) {
    case 'ACTIVATE':
      return {
        text: t('CONVERSATION.TIKTOKSHOP.PRODUCTS.STATUS.ACTIVATE'),
        class: 'bg-woot-600',
      };
    case 'FREEZE':
      return {
        text: t('CONVERSATION.TIKTOKSHOP.PRODUCTS.STATUS.FREEZE'),
        class: 'bg-violet-600',
      };
    case 'SELLER_DEACTIVATED':
    case 'PLATFORM_DEACTIVATED':
    case 'DELETED':
      return {
        text: t(`CONVERSATION.TIKTOKSHOP.PRODUCTS.STATUS.${product.status}`),
        class: 'bg-red-600',
      };
    default:
      return {
        text: product.status,
        class:
          'bg-orange-300 rounded-full text-gray-800 px-2 py-0.5 text-xs font-semibold',
      };
  }
};

const sendable = product => {
  return product.status === 'ACTIVATE' && product.stock !== 0;
};

const sendProduct = product => {
  store.dispatch('tiktokShop/sendProductMessage', {
    conversationID: props.currentChat.id,
    productID: product.productId,
  });
};

const searchProducts = () => {
  store.dispatch('tiktokShop/searchProducts', {
    conversationID: props.currentChat.id,
    searchQuery: searchQuery.value,
  });
};

onMounted(() => {
  searchProducts();
});
</script>

<template>
  <div class="flex flex-col h-full bg-slate-50 dark:bg-slate-800">
    <div id="search-product">
      <input
        v-model="searchQuery"
        type="text"
        :placeholder="$t('CONVERSATION.TIKTOKSHOP.PRODUCTS.SEARCH')"
        @keyup.enter="searchProducts"
      />
      <Icon
        class="absolute top-3 right-5 text-slate-500 dark:text-slate-500"
        icon="i-lucide-search"
      />
    </div>
    <div class="flex-1 overflow-y-auto">
      <LoadingState
        v-if="uiFlags.isSearchingProducts"
        :message="fetchingProductsMessage"
      />
      <EmptyMessage
        v-else-if="!products.length"
        :message="$t('CONVERSATION.TIKTOKSHOP.PRODUCTS.EMPTY_MESSAGE')"
      />
      <ul v-else class="p-2">
        <li
          v-for="product in products"
          :key="product.productId"
          class="product"
        >
          <p
            class="w-fit rounded-full text-white px-2 py-0.5 text-xs font-semibold"
            :class="productStatusMapping(product).class"
          >
            {{ productStatusMapping(product).text }}
          </p>
          <div class="flex flex-row pb-2">
            <div class="flex-none w-12 h-12">
              <img
                v-if="productImage(product)"
                :src="productImage(product)"
                :alt="product.name"
                class="rounded-lg object-cover"
              />
            </div>
            <div class="flex-auto ps-2 flex-col overflow-hidden">
              <p
                v-tooltip.top="product.name"
                class="line-clamp-1 w-full text-xs mb-0"
              >
                {{ product.name }}
              </p>
              <div class="flex flex-col">
                <span class="flex-1 text-slate-500 text-xs">
                  {{ product.sku }}
                </span>
                <span class="flex-1 text-slate-500 text-xs">
                  {{ t('CONVERSATION.TIKTOKSHOP.PRODUCTS.STOCK') }}
                  {{ product.stock }}
                </span>
              </div>
              <div class="flex-row text-end">
                <span
                  v-if="productPrice(product)"
                  class="font-semibold flex-1 items-center text-woot-600 ms-2"
                >
                  {{ productPrice(product) }}
                </span>
              </div>
            </div>
          </div>
          <div
            class="flex flex-row border-t border-slate-200 dark:border-slate-600 border-dashed pt-2"
          >
            <div>
              <small
                class="border bg-white px-2 py-0.5 rounded-full text-xs me-1 border-woot-600 text-woot-600 dark:bg-slate-900 dark:text-slate-300 dark:border-slate-300"
              >
                {{ product.meta?.brand_name }}
              </small>
            </div>
            <div class="flex-auto text-end">
              <button
                class="ms-2 text-white bg-woot-600 rounded-md py-1 px-5 text-sm hover:bg-woot-400 cursor-pointer"
                :disabled="!sendable(product)"
                @click="sendProduct(product)"
              >
                {{ t('CONVERSATION.TIKTOKSHOP.SEND') }}
              </button>
            </div>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
#search-product {
  @apply bg-white dark:bg-slate-900 px-2 py-1 relative border-b border-slate-100 dark:border-slate-800;
  input {
    @apply border-none rounded-3xl text-xs mb-0 outline-none pe-9 ps-5 py-0 h-8 dark:bg-slate-800;
  }
}

.product {
  @apply rounded-lg bg-white dark:bg-slate-900 my-2 px-2 py-3;
}
</style>
